import {observer} from "mobx-react-lite";
import {useContext, useEffect, useRef, useState} from "react";
import {AdminStore} from "./stores/admin_store";
import {AdminStoreContext} from "./protected_route";
import {AdminHeader} from "./components/admin_decorations";
import styled, {useTheme} from "styled-components";
import {CvvColors, CvvTheme, StyledProps, Theme} from "../theme";
import {DateFormatters} from "../utils";
import {
  MdAdd,
  MdCheck,
  MdClose,
  MdContentCopy,
  MdDelete,
  MdEdit,
  MdInsertDriveFile,
  MdPictureAsPdf,
  MdSave,
  MdWarning
} from "react-icons/md";
import {useNavigate} from "react-router-dom";
import {CvvPaymentType, CvvStrings} from "../strings";
import {AdminButton} from "./admin_scaffold";
import {admin_participants, billing, cvv_types, exports, mladi_voznik} from "../proto/compiled";
import {LoadingOverlay} from "../components/overlays";
import {UnovciBonDialog, YesNoDialog} from "../components/dialogs";
import {appStore, AppStoreContext} from "../index";
import {MladiVoznikStore} from "../stores/mladi_voznik_store";
import {useAutocompleteSuggestions} from "../forms/components/use_autocomplete_suggestions";
import {SLOVENIA_AUTOCOMPLETE_OPTIONS} from "../forms/components/gmaps";
import {toast} from "react-toastify";
import DatePicker from "react-date-picker";
import {AdminDatepickerStyles} from "../components/datepicker_styles";
import {makeAutoObservable} from "mobx";
import {proto} from "../proto/messages";
import {SocketApiAckStatus} from "proto_socket_typescript";
import {FileUploadInput, MultiFileUploadInput} from "../forms/components/file_upload";
import PaymentType = billing.PaymentType;

const ParticipantGrid = styled.div<{ columns: string }>`
  width: 100%;
  display: grid;
  grid-template-columns: ${({columns}) => columns};
  gap: 1px;
  margin-bottom: 10px;
`;

const ParticipantGridHeaderCell = styled.div<{ span?: number }>`
  display: flex;
  align-items: center;
  padding: 0 4px 0 4px;
  line-height: 27px;
  height: 27px;
  outline: solid 1px #CCCCCC;
  background-color: ${({theme}) => theme.content10};
  grid-column-end: span ${({span}) => span ?? 1};
  overflow: hidden;
  ${({theme}: StyledProps) => theme.textTheme.body.with({color: 'black', weight: 'bold'}).css};
`;

const ParticipantGridCell = styled.div<{ span?: number, appearance?: cvv_types.GridColumnStyle, dense?: boolean }>`
  display: flex;
  outline: solid 1px #CCCCCC;
  min-height: ${({dense}: StyledProps) => dense ? '20px' : '30px'};
  align-items: center;
  padding: 0 5px 0 5px;
  grid-column-end: span ${({span}) => span ?? 1};
  overflow: hidden;
  ${({theme, appearance, dense}: StyledProps) => {
    let color;
    if (dense) {
      color = 'white';
    } else {
      switch (appearance) {
        case cvv_types.GridColumnStyle.error:
          color = 'white';
          break;
        default:
          color = 'black';
          break;
      }
    }
    return theme.textTheme.body.with({color: color}).css;
  }};
  background-color: ${({theme, appearance, dense}: StyledProps) => {
    if (dense) return theme.content50;
    switch (appearance) {
      case cvv_types.GridColumnStyle.error:
        return '#CC0200';
      default:
        return 'transparent';
    }
  }};
`;

export const AdminParticipant = observer(() => {
  const [editing, setEditing] = useState(false);
  const store = useContext(AdminStoreContext) as AdminStore;
  const participant = store.participants.participant!;
  const summary = participant.summary!;
  const form = participant.form!;
  const formConfig = participant.formConfig!;
  const start = summary.tecajStart ? new Date(summary.tecajStart.ts as number) : undefined;
  const navigate = useNavigate();
  const theme = useTheme() as Theme;
  const saldo = ((participant.amountTotal ?? 0) - (participant.amountPaid ?? 0)) / 100;
  const children = !!form.step1?.children;
  let childrenObservable = false;
  useEffect(() => {
    if (!participant.form!.step3!.veljavnoVoznisko && form.step3!.napotilo !== mladi_voznik.KazenskeTockeNapotilo.zdravstveni_pregled) {
      form.step3!.napotilo = mladi_voznik.KazenskeTockeNapotilo.zdravstveni_pregled;
    } else if (participant.form!.step3!.veljavnoVoznisko && form.step3!.napotilo === mladi_voznik.KazenskeTockeNapotilo.zdravstveni_pregled) {
      form.step3!.napotilo = mladi_voznik.KazenskeTockeNapotilo.sodisce;
    }
  }, [participant.form!.step3!.veljavnoVoznisko]);

  return <>
    <AdminHeader>
      PODROBNOSTI O PRIJAVI
      <span style={{marginLeft: 'auto'}}/>
      {participant.availableExports?.some((template) => template.type === exports.ExportType.potrdilo_pred_udelezbo) &&
          <LoadingOverlay style={{marginLeft: '8px'}} scale={0} builder={(action) => <AdminButton
              style={{color: theme.content, borderColor: theme.primaryDark}}
              background={theme.primary}
              backgroundHover={'#ffe871'}
              onClick={() => {
                if (!summary.stevilkaPotrdila) {
                  appStore.showDialog(
                      <YesNoDialog result={async (result) => {
                        appStore.hideDialog();
                        if (result) {
                          await action(store.participants.downloadConfirmation(exports.ExportType.potrdilo_pred_udelezbo));
                          store.participants.setParticipant(summary.sessionId!);
                        }
                      }}>
                        <CvvTheme.textTheme.headline5.span>
                          Ali ste prepričani, da želite ustvariti potrdilo o prijavi?
                        </CvvTheme.textTheme.headline5.span>
                      </YesNoDialog>
                  );
                } else {
                  action(store.participants.downloadConfirmation(exports.ExportType.potrdilo_pred_udelezbo));
                }
              }}>
            {participant.availableExports?.find((template) => template.type === exports.ExportType.potrdilo_pred_udelezbo)?.name ?? 'Potrdilo o prijavi'}
          </AdminButton>}/>}
      {(summary.canAlwaysCreateCertificate || summary.daysPresent?.length === summary.nDays) && participant.availableExports?.some((template) => template.type === exports.ExportType.potrdilo) &&
          <LoadingOverlay style={{marginLeft: '8px'}} scale={0} builder={(action) => <AdminButton
              style={{color: theme.content, borderColor: theme.primaryDark}}
              background={theme.primary}
              backgroundHover={'#ffe871'}
              onClick={() => {
                if (!summary.stevilkaPotrdila) {
                  appStore.showDialog(
                      <YesNoDialog result={async (result) => {
                        appStore.hideDialog();
                        if (result) {
                          await action(store.participants.downloadConfirmation(exports.ExportType.potrdilo));
                          store.participants.setParticipant(summary.sessionId!);
                        }
                      }}>
                        <CvvTheme.textTheme.headline5.span>
                          Ali ste prepričani, da želite ustvariti potrdilo o udeležbi?
                        </CvvTheme.textTheme.headline5.span>
                      </YesNoDialog>
                  );
                } else {
                  action(store.participants.downloadConfirmation(exports.ExportType.potrdilo));
                }
              }}>
            {(participant.availableExports?.find((template) => template.type === exports.ExportType.potrdilo)?.name ?? 'Potrdilo o udeležbi') + (summary.stevilkaPotrdila ? ` (${summary.stevilkaPotrdila})` : '')}
          </AdminButton>}/>}
      {!summary.verified &&
          <LoadingOverlay scale={0} builder={(action) => <AdminButton
              style={{color: 'white', borderColor: 'white', backgroundColor: CvvColors.green}}
              background={theme.primary}
              backgroundHover={'#ffe871'}
              onClick={async () => {
                const response = await action(store.api.sendMessage(proto.TxAdminSetParticipantVerified.create({
                  sessionId: summary.sessionId
                }), {ack: true}));
                if (response.status === SocketApiAckStatus.success) {
                  store.participants.setParticipant(summary.sessionId!);
                  store.participants.load();
                } else {
                  toast.error(response.errorMessage ?? 'Neznana napaka');
                }
              }}>
            <MdCheck style={{marginBottom: '-2px'}}/> OZNAČI KOT PREVERJENO
          </AdminButton>
          }/>}
      {summary.paymentStatus !== billing.PaymentStatus.paid &&
          <LoadingOverlay scale={0} builder={(action) => <AdminButton
              style={{color: theme.content, borderColor: theme.primaryDark}}
              background={theme.primary}
              backgroundHover={'#ffe871'}
              onClick={async () => {
                const response = await action(store.api.sendMessage(proto.TxAdminSendPredracun.create({
                  sessionId: summary.sessionId
                }), {ack: true}));
                if (response.status === SocketApiAckStatus.success) {
                  toast.success('Poslano');
                  store.participants.setParticipant(summary.sessionId!);
                  store.participants.load();
                } else {
                  toast.error(response.errorMessage ?? 'Neznana napaka');
                }
              }}>
            Pošlji predračun na e-pošto
          </AdminButton>
          }/>}
      {!participant.summary?.archived && <span
          style={{
            marginLeft: '8px',
            cursor: 'pointer',
            height: '100%',
            display: 'flex',
            justifyContent: "center",
            alignItems: "center"
          }}
          onClick={() => {
            appStore.showDialog(
                <YesNoDialog result={(result) => {
                  appStore.hideDialog();
                  if (result) {
                    store.participants.archive(participant);
                  }
                }}>
                  <CvvTheme.textTheme.headline5.span>
                    <center>Ali ste prepričani, da želite arhivirati prijavo?</center>
                  </CvvTheme.textTheme.headline5.span>
                </YesNoDialog>
            )
          }}>
                <MdDelete style={{fontSize: '20px', fontWeight: 'bold'}}/>
            </span>}
      <span
          style={{
            marginLeft: '8px',
            marginTop: '-16px',
            marginBottom: '-16px',
            cursor: 'pointer',
            height: 'calc(100% + 12px)',
            display: 'flex',
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: editing ? theme.primary : 'transparent',
            color: editing ? theme.content : 'white',
            padding: editing ? '0 8px 0 8px' : undefined,
            transition: 'all 300ms'
          }}
          onClick={() => {
            if (editing) {
              store.api.sendMessage(proto.TxSaveMladiVoznikFormAdmin.create({
                sessionId: summary.sessionId,
                form: form
              }), {ack: true}).then((r) => {
                if (r.status !== SocketApiAckStatus.success) {
                  toast.error(r.errorMessage ?? 'Neznana napaka.');
                }
                store.participants.setParticipant(summary.sessionId!);
                store.participants.load();
              });
            }
            if (!editing && children && !childrenObservable && form.stepChildren!.children) {
              for (const child of form.stepChildren!.children) {
                makeAutoObservable(child);
                if (child.birthDate) {
                  makeAutoObservable(child.birthDate);
                }
              }
              childrenObservable = true;
            }
            if (!editing) {
              if (form.step1) {
                const ns = form.step1.nameSurname ?? '';
                if (formConfig.s1?.name && !form.step1.name && ns) {
                  form.step1.name = ns.split(' ')[0] || '';
                }
                if (formConfig.s1?.surname && !form.step1.surname && ns) {
                  form.step1.surname = ns.split(' ').slice(1).join(' ') || '';
                }
              }
              if (form.stepChildren?.children) {
                for (const c of form.stepChildren.children) {
                  const ns = c.nameSurname ?? '';
                  if (formConfig.stepChildren?.name && !c.name && ns) {
                    c.name = (ns.split(' ')[0] || '').toUpperCase();
                  }
                  if (formConfig.stepChildren?.surname && !c.surname && ns) {
                    c.surname = (ns.split(' ').slice(1).join(' ') || '').toUpperCase();
                  }
                }
              }
            }
            setEditing(!editing);
          }}>
                {!editing && <MdEdit style={{fontSize: '20px', fontWeight: 'bold'}}/>}
        {editing && <MdSave style={{fontSize: '20px', fontWeight: 'bold'}}/>}
            </span>
      <span
          style={{
            marginLeft: '8px',
            cursor: 'pointer',
            height: '100%',
            display: 'flex',
            justifyContent: "center",
            alignItems: "center"
          }}
          onClick={() => {
            if (editing) {
              store.participants.setParticipant(summary.sessionId!);
              store.participants.load();
              setEditing(false);
            } else {
              store.participants.setParticipant();
            }
          }}>
            <MdClose style={{fontSize: '20px', fontWeight: 'bold'}}/>
            </span>
    </AdminHeader>
    <ParticipantGrid columns={'auto auto'}>
      <ParticipantGridHeaderCell span={2}>
        PODATKI O TEČAJU
      </ParticipantGridHeaderCell>
      <ParticipantGridCell>
        {summary.tecajTipName?.toUpperCase()} - {summary.tecajName?.toUpperCase()}
      </ParticipantGridCell>
      <ParticipantGridCell
          onClick={() => {
            if (!editing) {
              setEditing(true);
            }
          }}
          style={{
            backgroundColor: start ? undefined : CvvColors.warnYellow,
            cursor: editing ? undefined : 'pointer'
          }}>
        DATUM{!(editing && participant.alternativeDates?.length) && (start ? <>: {summary.location} - {((summary.tecajDates?.length ?? 0) > 1) ?
              summary.tecajDates?.map((d) => `${DateFormatters.date(new Date(d.ts as number))} (${DateFormatters.time(new Date(d.ts as number))})`).join(', ')
              : `${DateFormatters.date(start)} (${DateFormatters.time(start)})`
          }
          </>
          : ' NI DOLOČEN')}
        {(editing && participant.alternativeDates?.length) &&
            <select style={{margin: 'auto 0 auto 8px'}} value={form.step2?.date?.id ?? 'no-date'}
                    onChange={(e) => form.step2!.date = participant.alternativeDates?.find(t => t.id === e.target.value)}>
              <option key={'alternative-date-none'} value={'no-date'}>
                --- Brez datuma ---
              </option>
              {participant.alternativeDates?.map((t) => <option key={'alternative-date-' + t.id!}
                                                                value={t.id!}>
                {t?.location} - {` `}
                {
                  (t?.dateTimes?.length ?? 0) > 1 ? (
                        t?.dateTimes?.map((d) => `${DateFormatters.date(new Date(d!.ts as number))} (${DateFormatters.time(new Date(d!.ts as number))})`).join(', ')
                    ) : `${DateFormatters.date(new Date(t.dateTime!.ts as number))} (${DateFormatters.time(new Date(t.dateTime!.ts as number))})`
                }
              </option>)}
            </select>}
      </ParticipantGridCell>
    </ParticipantGrid>
    <ParticipantGrid columns={'auto auto auto'}>
      <ParticipantGridHeaderCell span={3}>
        PODATKI VEZANI NA TEČAJ
      </ParticipantGridHeaderCell>
      <ParticipantGridCell>
        {participant.form?.prijatelj && `VOZILO JE NAJEL PRIJATELJ (${participant.form.prijatelj})`}
        {!participant.form?.prijatelj && <>
          VOZILO: {!editing && CvvStrings.vozilo[form!.step4!.vozilo as number]}
          {editing && <select style={{margin: 'auto 0 auto 8px'}} value={form!.step4!.vozilo!.toString()}
                              onChange={(e) => form!.step4!.vozilo = parseInt(e.target.value)}>
            <option value={cvv_types.Vozilo.svoje}>
              {CvvStrings.vozilo[cvv_types.Vozilo.svoje]}
            </option>
            {formConfig.s4?.najemVozila && <option value={cvv_types.Vozilo.najeto}>
              {CvvStrings.vozilo[cvv_types.Vozilo.najeto]}
            </option>}
            {formConfig.s4?.najemVozilaSPrijateljem && <option value={cvv_types.Vozilo.najeto_prijatelj}>
              {CvvStrings.vozilo[cvv_types.Vozilo.najeto_prijatelj]}
            </option>}
            {formConfig.s4?.najemVozilaZDrugoOsebo && <option value={cvv_types.Vozilo.najeto_druga_oseba}>
              {CvvStrings.vozilo[cvv_types.Vozilo.najeto_druga_oseba]}
            </option>}
          </select>}
        </>}
      </ParticipantGridCell>
      {formConfig.s4?.najemVozilaCategory && <ParticipantGridCell>
        KATEGORIJA
        VOZILA: {!editing && (CvvStrings.voziloCategory[form!.step4!.najemVozilaCategory as number] ?? CvvStrings.voziloCategory[mladi_voznik.NajemVozilaCategory.a1])}
        {editing && <select style={{margin: 'auto 0 auto 8px'}} value={form!.step4!.najemVozilaCategory!.toString()}
                            onChange={(e) => form!.step4!.najemVozilaCategory = parseInt(e.target.value)}>
          <option value={mladi_voznik.NajemVozilaCategory.a1}>
            {CvvStrings.voziloCategory[mladi_voznik.NajemVozilaCategory.a1]}
          </option>
          <option value={mladi_voznik.NajemVozilaCategory.a2}>
            {CvvStrings.voziloCategory[mladi_voznik.NajemVozilaCategory.a2]}
          </option>
        </select>}
      </ParticipantGridCell>}
      {participant.form?.step4?.vozilo === cvv_types.Vozilo.najeto_prijatelj && participant.form?.step4?.voziloPrijateljEmail &&
          <ParticipantGridCell>
            VOZILO PRIJATELJ
            E-MAIL: {!editing && (participant.form?.step4?.vozilo === cvv_types.Vozilo.najeto_prijatelj ? participant.form?.step4?.voziloPrijateljEmail : '/')}
            {editing && <input type={"text"}
                               style={{margin: 'auto 0 auto 8px'}}
                               value={participant.form?.step4?.voziloPrijateljEmail ?? ''}
                               onChange={(e) => {
                                 participant.form!.step4!.voziloPrijateljEmail = e.target.value;
                               }}/>
            }
          </ParticipantGridCell>}
      {participant.form?.step4?.vozilo === cvv_types.Vozilo.najeto_prijatelj && <ParticipantGridCell>
        VOZILO PRIJATELJ
        (ime in
        priimek): {!editing && (participant.form?.step4?.vozilo === cvv_types.Vozilo.najeto_prijatelj ? participant.form?.step4?.voziloPrijateljIme : '/')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step4?.voziloPrijateljIme ?? ''}
                           onChange={(e) => {
                             participant.form!.step4!.voziloPrijateljIme = e.target.value.toUpperCase();
                           }}/>
        }
      </ParticipantGridCell>}
      {formConfig.s4?.vSkupiniSPrijateljem && <ParticipantGridCell>
        {"V SKUPINI S PRIJATELJEM: "}
        {!editing && (participant.form?.step4?.vSkupiniSPrijateljem ? participant.form?.step4?.skupinaPrijateljIme : 'NE')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step4?.skupinaPrijateljIme ?? ''}
                           onChange={(e) => {
                             participant.form!.step4!.vSkupiniSPrijateljem = !!e.target.value.length;
                             participant.form!.step4!.skupinaPrijateljIme = e.target.value.toUpperCase();
                           }}/>
        }
      </ParticipantGridCell>}
      {formConfig.s3?.nameSurnameSopotnik && <ParticipantGridCell>
        IME IN PRIIMEK SOPOTNIKA/CE: {!editing && (participant.form?.step3?.nameSurnameSopotnik ?? '/')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step3?.nameSurnameSopotnik ?? ''}
                           onChange={(e) => {
                             participant.form!.step3!.nameSurnameSopotnik = e.target.value;
                           }}/>
        }
      </ParticipantGridCell>}
        {formConfig.s3?.motorModel && <ParticipantGridCell>
          MODEL MOTORJA: {!editing && (participant.form?.step3?.motorModel ?? '/')}
          {editing && <input type={"text"}
                             style={{margin: 'auto 0 auto 8px'}}
                             value={participant.form?.step3?.motorModel ?? ''}
                             onChange={(e) => {
                               participant.form!.step3!.motorModel = e.target.value;
                             }}/>
          }
        </ParticipantGridCell>}
      {formConfig.s4?.kosilo && (
          <ParticipantGridCell>
            KOSILO
            {participant.clanskaStevilka
                ? ': ZASTONJ'
                : editing
                    ? (
                        <select
                            style={{margin: 'auto 0 auto 8px'}}
                            value={participant.form?.step4?.kosiloMultipleChoice ?? (participant.form?.step4?.kosilo ? mladi_voznik.Kosilo.da : mladi_voznik.Kosilo.ne)}
                            onChange={(e) => {
                              participant.form!.step4!.kosiloMultipleChoice = parseInt(e.target.value);
                            }}
                        >
                          <option value={mladi_voznik.Kosilo.ne}>NE</option>
                          <option value={mladi_voznik.Kosilo.da}>DA</option>
                          <option value={mladi_voznik.Kosilo.da_bo_clan}>BREZPLAČNO (se bo včlanil)</option>
                          <option value={mladi_voznik.Kosilo.da_drajv}>BREZPLAČNO (časti DRAJV)</option>
                        </select>
                    )
                    : (
                        ': ' +
                        (participant.form!.step4!.kosiloMultipleChoice === mladi_voznik.Kosilo.da_drajv
                                ? 'ZASTONJ (časti DRAJV)'
                                : participant.form!.step4!.kosiloMultipleChoice === mladi_voznik.Kosilo.da_bo_clan
                                    ? 'ZASTONJ (se bo včlanil)'
                                    : participant.form!.step4!.kosiloMultipleChoice === mladi_voznik.Kosilo.da
                                        ? 'DA'
                                        : participant.form!.step4?.kosilo ? (participant.form!.step4?.clanstvo ? 'ZASTONJ (se bo včlanil)' : 'NE') : 'NE'
                        )
                    )
            }
          </ParticipantGridCell>
      )}
      {formConfig.s4?.opomba && <ParticipantGridCell>
        OPOMBA: {!editing && form.step4?.opomba}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step4?.opomba ?? ''}
                           onChange={(e) => {
                             participant.form!.step4!.opomba = e.target.value;
                           }}/>}
      </ParticipantGridCell>}
      {!participant.clanskaStevilka && <ParticipantGridCell>
        {editing && <input type={"checkbox"}
                           style={{marginRight: '8px'}}
                           checked={participant.form?.step4?.clanstvo!}
                           onChange={() => participant.form!.step4!.clanstvo = !participant.form?.step4?.clanstvo}/>}
        SKLENI ČLANSTVO{!editing ? ': ' + (participant.form!.step4!.clanstvo ? 'DA' : 'NE') : undefined}
      </ParticipantGridCell>}
      {participant.clanskaStevilka && <ParticipantGridCell>
        ČLANSKA ŠTEVILKA: {participant.clanskaStevilka}
      </ParticipantGridCell>}
      {formConfig.s4?.eIzpitKoda && <ParticipantGridCell>
        E-IZPIT KODA: {!editing && (participant.form?.step4?.eIzpitKoda ?? '/')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step4?.eIzpitKoda ?? ''}
                           onChange={(e) => {
                             participant.form!.step4!.eIzpitKoda = e.target.value;
                           }}/>
        }
      </ParticipantGridCell>}
      {formConfig.s6?.kjeSteSlisaliZaNas && <ParticipantGridCell>
        KJE STE SLIŠALI ZA NAS: {!editing && (participant.form?.step6?.kjeSteSlisaliZaNas ?? '/')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step6?.kjeSteSlisaliZaNas ?? ''}
                           onChange={(e) => {
                             participant.form!.step6!.kjeSteSlisaliZaNas = e.target.value;
                           }}/>
        }
      </ParticipantGridCell>}
      {formConfig.s6?.nacinKontaktiranja && <ParticipantGridCell>
        NAČIN KONTAKTIRANJA: {!editing && (participant.form?.step6?.nacinKontaktiranja ?? '/')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step6?.nacinKontaktiranja ?? ''}
                           onChange={(e) => {
                             participant.form!.step6!.nacinKontaktiranja = e.target.value;
                           }}/>
        }
      </ParticipantGridCell>}
      {formConfig.s6?.promocijskaKoda && <ParticipantGridCell>
        PROMOCIJSKA KODA: {!editing && (participant.form?.step6?.promoCode ?? '/')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step6?.promoCode ?? ''}
                           onChange={(e) => {
                             participant.form!.step6!.promoCode = e.target.value;
                           }}/>
        }
      </ParticipantGridCell>}
    </ParticipantGrid>
    <ParticipantGrid columns={'auto auto auto'}>
      <ParticipantGridHeaderCell span={3}>
        PODATKI O OSEBI
      </ParticipantGridHeaderCell>
      {formConfig.s1?.nameSurname && <ParticipantGridCell>
        IME IN PRIIMEK: {!editing && form.step1?.nameSurname}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step1?.nameSurname ?? ''}
                           onChange={(e) => {
                             participant.form!.step1!.nameSurname = e.target.value;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s1?.name && <ParticipantGridCell>
        IME: {!editing && (form.step1?.name || (form.step1?.nameSurname ?? '').split(' ')[0] || '')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={form.step1?.name || (form.step1?.nameSurname ?? '').split(' ')[0] || ''}
                           onChange={(e) => {
                             form.step1!.name = e.target.value;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s1?.surname && <ParticipantGridCell>
        PRIIMEK: {!editing && (form.step1?.surname || (form.step1?.nameSurname ?? '').split(' ').slice(1).join(' ') || '')}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={form.step1?.surname || (form.step1?.nameSurname ?? '').split(' ').slice(1).join(' ') || ''}
                           onChange={(e) => {
                             form.step1!.surname = e.target.value;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s1?.address && <ParticipantGridCell>
        NASLOV: {!editing && form.step1?.address?.address}
        {editing && <AdminAddressPicker address={form.step1?.address!}/>}
      </ParticipantGridCell>}
      {formConfig.s1?.mailingAddress && <ParticipantGridCell>
        NASLOV ZA POŠILJANJE: {!editing && form.step1?.mailingAddress?.address}
        {editing && <AdminAddressPicker address={form.step1?.mailingAddress!}/>}
      </ParticipantGridCell>}
      {formConfig.s1?.email && <ParticipantGridCell>
        E-MAIL: {!editing && form.step1?.email}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step1?.email ?? ''}
                           onChange={(e) => {
                             participant.form!.step1!.email = e.target.value;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s1?.children && <ParticipantGridCell>
        ŠTEVILO OTROK: {form.step1?.children}
        {editing && <span
            style={{
              marginLeft: 'auto',
              cursor: 'pointer',
              height: '100%',
              display: 'flex',
              justifyContent: "center",
              alignItems: "center"
            }}
            onClick={() => {
              form.step1!.children = (form.step1!.children ?? 0) + 1;
              if (!form.stepChildren?.children) {
                form.stepChildren!.children = [];
              }
              if (form.stepChildren!.children.length < form.step1!.children!) {
                form.stepChildren!.children.push({
                  name: '',
                  surname: '',
                  birthDate: undefined,
                  height: undefined,
                  weight: undefined
                });
                makeAutoObservable(form.stepChildren!.children[form.stepChildren!.children.length - 1]);
              }
            }}
        >
                    <MdAdd style={{fontSize: '15px', fontWeight: 'bolder'}}></MdAdd>
                </span>}
      </ParticipantGridCell>}
      {formConfig.s1?.phone && <ParticipantGridCell>
        TELEFON: {!editing && form.step1?.phone}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step1?.phone ?? ''}
                           onChange={(e) => {
                             participant.form!.step1!.phone = e.target.value;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s1?.isStudent && <ParticipantGridCell>
        {editing && <input type={"checkbox"}
                           style={{marginRight: '8px'}}
                           checked={participant.form?.step1?.isStudent!}
                           onChange={() => participant.form!.step1!.isStudent = !participant.form?.step1?.isStudent}/>}
        ŠTUDENT / UPOKOJENEC{!editing ? ': ' + (participant.form!.step1!.isStudent ? 'DA' : 'NE') : undefined}
      </ParticipantGridCell>}
      {formConfig.s3?.emso && <ParticipantGridCell>
        EMŠO: {!editing && form.step3?.emso}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step3?.emso ?? ''}
                           onChange={(e) => {
                             participant.form!.step3!.emso = e.target.value;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s3?.stVozniske && <ParticipantGridCell>
        ŠTEVILKA VOZNIŠKEGA DOVOLJENJA: {!editing && form.step3?.stVozniske}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step3?.stVozniske ?? ''}
                           onChange={(e) => {
                             participant.form!.step3!.stVozniske = e.target.value;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s3?.krajRojstva && <ParticipantGridCell>
        KRAJ ROJSTVA: {!editing && form.step3?.krajRojstva}
        {editing && <input type={"text"}
                           style={{margin: 'auto 0 auto 8px'}}
                           value={participant.form?.step3?.krajRojstva ?? ''}
                           onChange={(e) => {
                             participant.form!.step3!.krajRojstva = e.target.value.toUpperCase();
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s3?.veljavnoVoznisko && <ParticipantGridCell>
        {editing && <input type={"checkbox"}
                           style={{marginRight: '8px'}}
                           checked={participant.form?.step3?.veljavnoVoznisko!}
                           onChange={() => participant.form!.step3!.veljavnoVoznisko = !participant.form?.step3?.veljavnoVoznisko}/>}
        VELJAVNO
        VOZNIŠKO{!editing ? ': ' + (participant.form!.step3!.veljavnoVoznisko ? 'DA' : 'NE') : undefined}
      </ParticipantGridCell>}
      {formConfig.s3?.veljavnoVozniskoKazenskeTocke && <>
        <ParticipantGridCell>
          {editing && <input type={"checkbox"}
                             style={{marginRight: '8px'}}
                             checked={participant.form?.step3?.veljavnoVoznisko!}
                             onChange={() => {
                               participant.form!.step3!.veljavnoVoznisko = !participant.form?.step3?.veljavnoVoznisko;
                               if (!participant.form!.step3!.veljavnoVoznisko) {
                                 form.step3!.napotilo = mladi_voznik.KazenskeTockeNapotilo.zdravstveni_pregled;
                               }
                             }}/>}
          VELJAVNO
          VOZNIŠKO{!editing ? ': ' + (participant.form!.step3!.veljavnoVoznisko ? 'DA' : 'NE') : undefined}
        </ParticipantGridCell>
        <ParticipantGridCell>
          NAPOTILO: {!editing && CvvStrings.napotilo[form!.step3!.napotilo as number]}
          {editing && <select style={{margin: 'auto 0 auto 8px'}} value={form!.step3!.napotilo!.toString()}
                              onChange={(e) => form!.step3!.napotilo = parseInt(e.target.value)}>
            {form.step3?.veljavnoVoznisko && <option value={mladi_voznik.KazenskeTockeNapotilo.sodisce}>
              {CvvStrings.napotilo[mladi_voznik.KazenskeTockeNapotilo.sodisce]}
            </option>}
            {!form.step3?.veljavnoVoznisko && <option value={mladi_voznik.KazenskeTockeNapotilo.zdravstveni_pregled}>
              {CvvStrings.napotilo[mladi_voznik.KazenskeTockeNapotilo.zdravstveni_pregled]}
            </option>}
            {form.step3?.veljavnoVoznisko &&
                <option value={mladi_voznik.KazenskeTockeNapotilo.prostovoljno}>
                  {CvvStrings.napotilo[mladi_voznik.KazenskeTockeNapotilo.prostovoljno]}
                </option>
            }
          </select>}
        </ParticipantGridCell>

        {form.step3?.napotilo === mladi_voznik.KazenskeTockeNapotilo.sodisce && <>
          <ParticipantGridCell>
            ŠTEVILKA NAPOTILA: {!editing && form.step3?.napotiloStevilka}
            {editing && <input type={"text"}
                               style={{margin: 'auto 0 auto 8px'}}
                               value={participant.form?.step3?.napotiloStevilka ?? ''}
                               onChange={(e) => {
                                 participant.form!.step3!.napotiloStevilka = e.target.value;
                               }}/>}
          </ParticipantGridCell>
          <ParticipantGridCell>
            PRISTOJNO SODIŠČE: {!editing && form.step3?.napotiloPristojnoSodisce}
            {editing && <input type={"text"}
                               style={{margin: 'auto 0 auto 8px'}}
                               value={participant.form?.step3?.napotiloPristojnoSodisce ?? ''}
                               onChange={(e) => {
                                 participant.form!.step3!.napotiloPristojnoSodisce = e.target.value.toUpperCase();
                               }}/>}
          </ParticipantGridCell>
        </>}
        {form.step3?.napotilo === mladi_voznik.KazenskeTockeNapotilo.prostovoljno && <>
          <ParticipantGridCell>
            ŠTEVILO KAZENSKIH T.: {!editing && form.step3?.stKazenskihTock}
            {editing && <input type={"number"}
                               style={{margin: 'auto 0 auto 8px'}}
                               value={(participant.form?.step3?.stKazenskihTock ?? 0)?.toString()}
                               onChange={(e) => {
                                 participant.form!.step3!.stKazenskihTock = e.target.valueAsNumber;
                               }}/>}
          </ParticipantGridCell>
          <ParticipantGridCell>
            RAZLOG ZA KAZENSKE T.: {!editing && form.step3?.kazenskeTockeRazlog}
            {editing && <input type={"text"}
                               style={{margin: 'auto 0 auto 8px'}}
                               value={participant.form?.step3?.kazenskeTockeRazlog ?? ''}
                               onChange={(e) => {
                                 participant.form!.step3!.kazenskeTockeRazlog = e.target.value;
                               }}/>}
          </ParticipantGridCell>
        </>}

        {(form.step3?.napotilo === mladi_voznik.KazenskeTockeNapotilo.sodisce || form.step3?.napotilo === mladi_voznik.KazenskeTockeNapotilo.zdravstveni_pregled) &&
            <ParticipantGridCell style={{overflow: 'visible'}}>
              DATUM
              NAPOTILA: {!editing && (form?.step3?.napotiloDatum?.ts ? DateFormatters.date(new Date(form?.step3?.napotiloDatum?.ts as number)) : '/')}
              {editing && <AdminDatepickerStyles style={{margin: 'auto 0 auto 8px', lineHeight: '20px'}}>
                <DatePicker
                    value={form?.step3?.napotiloDatum?.ts ? new Date(form?.step3?.napotiloDatum?.ts as number) : undefined}
                    format={'d. M. y'}
                    onChange={(date?: Date) => {
                      if (!form!.step3!.napotiloDatum) {
                        form!.step3!.napotiloDatum = cvv_types.Date.create();
                        makeAutoObservable(form!.step3!.napotiloDatum);
                      }
                      if (!date) {
                        form!.step3!.napotiloDatum!.ts = undefined;
                        return;
                      }
                      form!.step3!.napotiloDatum!.ts = date.getTime();
                    }}/>
              </AdminDatepickerStyles>}
            </ParticipantGridCell>}
        {(form.step3?.napotilo === mladi_voznik.KazenskeTockeNapotilo.sodisce || form.step3?.napotilo === mladi_voznik.KazenskeTockeNapotilo.zdravstveni_pregled) &&
            <ParticipantGridCell>
              {form.step3?.napotilo === mladi_voznik.KazenskeTockeNapotilo.sodisce ? 'SKLEP SODIŠČA' : 'ZDRAVNIŠKO SPRIČEVALO'}:
              {formConfig.s3?.napotiloDokumenti
                ? <>
                    {!editing && (form.step3?.napotiloDokumenti?.length
                        ? <div style={{marginLeft: '8px', display: 'flex', flexDirection: 'column', gap: '4px'}}>
                            {form.step3.napotiloDokumenti.map((doc, i) => (
                              doc.src
                                ? <a key={doc.id ?? i} href={doc.src} target="_blank" rel="noopener noreferrer"
                                     style={{color: theme.content, display: 'flex', alignItems: 'center'}}>
                                    <MdInsertDriveFile style={{marginRight: '4px'}}/>{doc.name}
                                  </a>
                                : <span key={doc.id ?? i} style={{display: 'flex', alignItems: 'center'}}>
                                    <MdInsertDriveFile style={{marginRight: '4px'}}/>{doc.name}
                                  </span>
                            ))}
                          </div>
                        : <span style={{marginLeft: '8px', color: theme.content50}}>NI PRILOŽENO</span>
                    )}
                    {editing && <div style={{marginLeft: '8px', flex: 1}}>
                      <MultiFileUploadInput
                          files={form.step3?.napotiloDokumenti ?? []}
                          onFilesChanged={(fs) => form.step3!.napotiloDokumenti = fs}
                          extensions={['pdf', 'jpg', 'jpeg', 'png']}
                          maxFiles={10}
                          compact
                      />
                    </div>}
                  </>
                : <>
                    {!editing && (form.step3?.napotiloDokument?.id
                            ? (form.step3.napotiloDokument.src
                                ? <a href={form.step3.napotiloDokument.src} target="_blank" rel="noopener noreferrer"
                                     style={{marginLeft: '8px', color: theme.content, display: 'flex', alignItems: 'center'}}>
                                  <MdInsertDriveFile style={{marginRight: '4px'}}/>{form.step3.napotiloDokument.name}
                                </a>
                                : <span style={{marginLeft: '8px', display: 'flex', alignItems: 'center'}}>
                          <MdInsertDriveFile style={{marginRight: '4px'}}/>{form.step3.napotiloDokument.name}
                        </span>)
                            : <span style={{marginLeft: '8px', color: theme.content50}}>NI PRILOŽENO</span>
                    )}
                    {editing && <div style={{marginLeft: '8px', flex: 1}}>
                      <FileUploadInput
                          file={form.step3?.napotiloDokument ?? undefined}
                          onFileChanged={(f) => form.step3!.napotiloDokument = f}
                          extensions={['pdf', 'jpg', 'jpeg', 'png']}
                          compact
                      />
                    </div>}
                  </>
              }
            </ParticipantGridCell>}
      </>}
      {formConfig.s3?.datumIzdajeVozniske && <ParticipantGridCell style={{overflow: 'visible'}}>
        DATUM IZDAJE
        VOZNIŠKE: {!editing && (participant.form?.step3?.datumIzdajeVozniske?.ts ? DateFormatters.date(new Date(participant.form?.step3?.datumIzdajeVozniske?.ts as number)) : '/')}
        {editing && <AdminDatepickerStyles style={{margin: 'auto 0 auto 8px', lineHeight: '20px'}}>
          <DatePicker
              value={participant.form?.step3?.datumIzdajeVozniske?.ts ? new Date(participant.form?.step3?.datumIzdajeVozniske?.ts as number) : undefined}
              format={'d. M. y'}
              onChange={(date?: Date) => {
                if (!participant.form!.step3!.datumIzdajeVozniske) {
                  participant.form!.step3!.datumIzdajeVozniske = cvv_types.Date.create();
                  makeAutoObservable(participant.form!.step3!.datumIzdajeVozniske);
                }
                if (!date) {
                  participant.form!.step3!.datumIzdajeVozniske!.ts = undefined;
                  return;
                }
                participant.form!.step3!.datumIzdajeVozniske!.ts = date.getTime();
              }}/>
        </AdminDatepickerStyles>}
      </ParticipantGridCell>}
      {formConfig.s3?.datumOpravljenegaIntenzivnegaTecaja && <ParticipantGridCell style={{overflow: 'visible'}}>
        DATUM OP. INT.
        TEČAJA: {!editing && (participant.form?.step3?.datumOpravljenegaIntenzivnegaTecaja?.ts ? DateFormatters.date(new Date(participant.form?.step3?.datumOpravljenegaIntenzivnegaTecaja?.ts as number)) : '/')}
        {editing && <AdminDatepickerStyles style={{margin: 'auto 0 auto 8px', lineHeight: '20px'}}>
          <DatePicker
              value={participant.form?.step3?.datumOpravljenegaIntenzivnegaTecaja?.ts ? new Date(participant.form?.step3?.datumOpravljenegaIntenzivnegaTecaja?.ts as number) : undefined}
              format={'d. M. y'}
              onChange={(date?: Date) => {
                if (!participant.form!.step3!.datumOpravljenegaIntenzivnegaTecaja) {
                  participant.form!.step3!.datumOpravljenegaIntenzivnegaTecaja = cvv_types.Date.create();
                  makeAutoObservable(participant.form!.step3!.datumOpravljenegaIntenzivnegaTecaja);
                }
                if (!date) {
                  participant.form!.step3!.datumOpravljenegaIntenzivnegaTecaja!.ts = undefined;
                  return;
                }
                participant.form!.step3!.datumOpravljenegaIntenzivnegaTecaja!.ts = date.getTime();
              }}/>
        </AdminDatepickerStyles>}
      </ParticipantGridCell>}
      {formConfig.s3?.letoOpravljenegaIntenzivnegaTecaja && <ParticipantGridCell>
        LETO OP. INT. TEČAJA: {!editing && (participant.form?.step3?.letoOpravljenegaIntenzivnegaTecaja || '/')}
        {editing && <input type={"number"}
                           style={{margin: 'auto 0 auto 8px', width: '80px'}}
                           min={1900}
                           max={new Date().getFullYear()}
                           value={participant.form?.step3?.letoOpravljenegaIntenzivnegaTecaja || ''}
                           onChange={(e) => {
                             participant.form!.step3!.letoOpravljenegaIntenzivnegaTecaja = e.target.valueAsNumber || 0;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s3?.letoOpravljene_1Stopnje && <ParticipantGridCell>
        LETO OP. 1. STOPNJE: {!editing && (participant.form?.step3?.letoOpravljene_1Stopnje || '/')}
        {editing && <input type={"number"}
                           style={{margin: 'auto 0 auto 8px', width: '80px'}}
                           min={1900}
                           max={new Date().getFullYear()}
                           value={participant.form?.step3?.letoOpravljene_1Stopnje || ''}
                           onChange={(e) => {
                             participant.form!.step3!.letoOpravljene_1Stopnje = e.target.valueAsNumber || 0;
                           }}/>}
      </ParticipantGridCell>}
      {formConfig.s3?.letoOpravljenegaNadaljevalnegaTecaja && <ParticipantGridCell>
        LETO OP. NAD. TEČAJA: {!editing && (participant.form?.step3?.letoOpravljenegaNadaljevalnegaTecaja || '/')}
        {editing && <input type={"number"}
                           style={{margin: 'auto 0 auto 8px', width: '80px'}}
                           min={1900}
                           max={new Date().getFullYear()}
                           value={participant.form?.step3?.letoOpravljenegaNadaljevalnegaTecaja || ''}
                           onChange={(e) => {
                             participant.form!.step3!.letoOpravljenegaNadaljevalnegaTecaja = e.target.valueAsNumber || 0;
                           }}/>}
      </ParticipantGridCell>}
    </ParticipantGrid>
    {formConfig.s1?.children &&
        participant.form?.stepChildren?.children && participant.form?.stepChildren?.children.map((c, i) => {
          return <ParticipantGrid columns={'auto auto auto'}>
            <ParticipantGridHeaderCell span={3} key={i}>
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                width: '100%',
                alignItems: 'center',
              }}>
                <div>PODATKI O OTROCIH - OTROK {i + 1}</div>
                {editing && <span
                    style={{
                      cursor: 'pointer',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => {
                      appStore.showDialog(
                          <YesNoDialog result={(result) => {
                            appStore.hideDialog();
                            if (result) {
                              participant.form!.stepChildren!.children!.splice(i, 1);
                              participant.form!.step1!.children! -= 1;
                            }
                          }}>
                            <CvvTheme.textTheme.headline5.span>
                              <center>Ali ste prepričani, da želite izbrisati podatke o
                                otroku {i + 1}?
                              </center>
                            </CvvTheme.textTheme.headline5.span>
                          </YesNoDialog>
                      )
                    }}>
                                <MdDelete style={{fontWeight: 'bold', fontSize: '15px'}}/>
                            </span>}
              </div>
            </ParticipantGridHeaderCell>
            {
                formConfig.stepChildren?.nameSurname && <ParticipantGridCell key={`child-${i}-namesurname`}>
                  IME IN PRIIMEK: {!editing && (c.nameSurname ?? '')}
                  {editing && <input type={'text'}
                                     style={{margin: 'auto 0 auto 8px'}}
                                     value={c.nameSurname ?? ''}
                                     onChange={(e) => {
                                       c.nameSurname = e.target.value.toUpperCase();
                                     }}/>}
                </ParticipantGridCell>
            }
            {
                formConfig.stepChildren?.name && <ParticipantGridCell key={`child-${i}-name`}>
                  IME: {!editing && (c.name || (c.nameSurname ?? '').split(' ')[0] || '')}
                  {editing && <input type={'text'}
                                     style={{margin: 'auto 0 auto 8px'}}
                                     value={c.name || (c.nameSurname ?? '').split(' ')[0] || ''}
                                     onChange={(e) => {
                                       c.name = e.target.value.toUpperCase();
                                     }}/>}
                </ParticipantGridCell>
            }
            {
                formConfig.stepChildren?.surname && <ParticipantGridCell key={`child-${i}-surname`}>
                  PRIIMEK: {!editing && (c.surname || (c.nameSurname ?? '').split(' ').slice(1).join(' ') || '')}
                  {editing && <input type={'text'}
                                     style={{margin: 'auto 0 auto 8px'}}
                                     value={c.surname || (c.nameSurname ?? '').split(' ').slice(1).join(' ') || ''}
                                     onChange={(e) => {
                                       c.surname = e.target.value.toUpperCase();
                                     }}/>}
                </ParticipantGridCell>
            }
            {
                formConfig.stepChildren?.birthDate &&
                <ParticipantGridCell style={{overflow: 'visible'}} key={`child-${i}-birthdate`}>
                  DATUM
                  ROJSTVA: {!editing && (c.birthDate && c.birthDate.ts ? DateFormatters.date(new Date(c.birthDate.ts as number)) : '/')}
                  {editing && <AdminDatepickerStyles style={{margin: 'auto 0 auto 8px', lineHeight: '20px'}}>
                    <DatePicker
                        value={c.birthDate && c.birthDate.ts ? new Date(c.birthDate.ts as number) : undefined}
                        format={'d. M. y'}
                        onChange={(date?: Date) => {
                          if (!c.birthDate) {
                            c.birthDate = cvv_types.Date.create();
                            makeAutoObservable(c.birthDate);
                          }
                          if (!date) {
                            c.birthDate.ts = undefined;
                            return;
                          }
                          c.birthDate.ts = date.getTime();
                        }}/>
                  </AdminDatepickerStyles>}
                </ParticipantGridCell>
            }
            {
                formConfig.stepChildren?.height && <ParticipantGridCell key={`child-${i}-height`}>
                  VIŠINA: {!editing && c.height}
                  {editing && <input type={"number"}
                                     style={{margin: 'auto 8px'}}
                                     value={c.height ?? ''}
                                     onChange={(e) => {
                                       if (!e.target.value) {
                                         c.height = undefined;
                                         return;
                                       }
                                       c.height = Number(e.target.value);
                                     }}/>} cm
                </ParticipantGridCell>
            }
            {
                formConfig.stepChildren?.weight && <ParticipantGridCell key={`child-${i}-weight`}>
                  TEŽA: {!editing && c.weight}
                  {editing && <input type={"number"}
                                     style={{margin: 'auto 8px'}}
                                     value={c.weight ?? ''}
                                     onChange={(e) => {
                                       if (!e.target.value) {
                                         c.weight = undefined;
                                         return;
                                       }
                                       c.weight = Number(e.target.value);
                                     }}/>} kg
                </ParticipantGridCell>
            }
          </ParticipantGrid>
        })
    }

    <ParticipantGrid columns={'auto auto auto auto'}>
      <ParticipantGridHeaderCell span={4}>
        {saldo !== 0 && <MdWarning style={{marginRight: '4px', color: theme.error}}/>}
        PODATKI O PLAČILU
        <div style={{marginLeft: 'auto'}}/>
        {saldo < 0 && <LoadingOverlay style={{marginLeft: '8px'}} scale={0} builder={(action) => <AdminButton
            style={{color: theme.content, borderColor: theme.primaryDark}}
            background={theme.primary}
            backgroundHover={'#ffe871'}
            onClick={async () => {
              const result = await action(store.api.sendMessage(proto.TxAdminPaymentAtCvv.create({sessionId: summary.sessionId}), {ack: true}));
              if (result.status !== SocketApiAckStatus.success) {
                toast.error(result.errorMessage);
              } else {
                store.participants.setParticipant(summary.sessionId!);
                store.participants.load();
              }
            }}>
          Vračilo preplačanega zneska
        </AdminButton>}/>}
        {saldo > 0 && <AdminButton
            style={{color: theme.content, borderColor: theme.primaryDark}}
            background={theme.primary}
            backgroundHover={'#ffe871'}
            onClick={() => {
              appStore.dialog = <UnovciBonDialog sessionId={summary.sessionId!}/>;
            }}>
          Unovči darilno kartico
        </AdminButton>}
        {saldo > 0 && <LoadingOverlay style={{marginLeft: '8px'}} scale={0} builder={(action) => <AdminButton
            style={{color: theme.content, borderColor: theme.primaryDark}}
            background={theme.primary}
            backgroundHover={'#ffe871'}
            onClick={async () => {
              const result = await action(store.api.sendMessage(proto.TxAdminPaymentAtCvv.create({sessionId: summary.sessionId}), {ack: true}));
              if (result.status !== SocketApiAckStatus.success) {
                toast.error(result.errorMessage);
              } else {
                store.participants.setParticipant(summary.sessionId!);
                store.participants.load();
              }
            }}>
          Plačilo na CVV
        </AdminButton>}/>}
      </ParticipantGridHeaderCell>
      <ParticipantGridCell span={summary.isGratis ? 4 : 1}>{summary.isGratis ? 'BREZPLAČNO' : `SKLIC: ${summary.sklic}`}</ParticipantGridCell>
      {!summary.isGratis && <ParticipantGridCell>IZBRANI TIP PLAČILA: {CvvPaymentType[summary.paymentType!]}</ParticipantGridCell>}
      {!summary.isGratis && <ParticipantGridCell span={2}>PLAČANO {(participant.amountPaid ?? 0) / 100}€
        OD {(participant.amountTotal ?? 0) / 100}€,
        SALDO:&nbsp;
        <b>{saldo}€</b></ParticipantGridCell>}
      {participant.payments?.map((p: admin_participants.IPaymentEntry, i: number) => {
        let span = 3;
        const isError = p.bankartPaymentStatus === billing.BankartPaymentStatus.bankart_error;
        const errorStyle = isError ? {color: '#c00', textDecoration: 'line-through'} : {};
        const children = [
          <ParticipantGridCell
              dense={true} style={{fontWeight: 'bold', ...errorStyle}}
              key={'payment-a-' + i}>{DateFormatters.dateTime(new Date(p.date?.ts as number))}</ParticipantGridCell>
        ];
        span--;
        let content;
        if (p.type === billing.PaymentType.darilni_bon) {
          content = `DARILNA KARTICA: ${p.darilniBon}`
        } else if (p.type === billing.PaymentType.gotovina) {
          content = p.amount! > 0 ? 'PLAČILO NA CVV' : 'VRAČILO';
        } else if (p.type === billing.PaymentType.kreditna) {
          content = `KREDITNA KARTICA (${p.bankartPaymentId})`
        }
        children.push(<ParticipantGridCell span={2} dense={true} style={{fontWeight: 'bold', ...errorStyle}}
                                           key={'payment-b-' + i}>
          {content}
          {isError && ' [NAPAKA]'}
          {p.comment && ` (${p.comment})`}
        </ParticipantGridCell>);
        span--;
        children.push(<ParticipantGridCell style={{fontWeight: 'bold', ...errorStyle}} dense={true} span={span}
                                           key={'payment-c-' + i}>{p.amount! / 100}€</ParticipantGridCell>);
        return children;
      })}
      <ParticipantGridCell span={1} style={{backgroundColor: CvvColors.shade, fontWeight: 'bold'}}>
        DOBAVNICA: {!!summary.navInvoiceProforma ? summary.navInvoiceProforma : '/'}
      </ParticipantGridCell>
      <ParticipantGridCell span={1} style={{backgroundColor: CvvColors.shade, fontWeight: 'bold'}}>
        AVANSNI RAČUN: {!!summary.navInvoiceAdvance ?
          <LoadingOverlay style={{display: 'inline-flex', marginLeft: '4px'}} scale={0} builder={(action) =>
              <span style={{display: 'inline-flex', alignItems: 'center', color: theme.content, gap: '6px'}}>
              <span style={{cursor: 'pointer', display: 'flex', alignItems: 'center', color: theme.content}}
                    onClick={() => action(store.participants.downloadInvoicePdf(admin_participants.InvoiceType.INVOICE_TYPE_ADVANCE))}>
                <MdPictureAsPdf style={{marginRight: '4px'}}/>{summary.navInvoiceAdvance}
              </span>
                {!!(summary.navTransactionId && summary.navInvoiceAdvance) &&
                    <span style={{display: 'inline-flex', alignItems: 'center', color: theme.content50}}>
                {(() => {
                  const navCopyText = `${summary.navTransactionId}|${summary.navInvoiceAdvance}`;
                  return <>
                    (<span style={{fontFamily: 'monospace'}}>{navCopyText}</span>
                    <MdContentCopy
                        style={{marginLeft: '4px', cursor: 'pointer'}}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(navCopyText);
                          toast.success('Kopirano v odložišče.');
                        }}/>)
                  </>;
                })()}
              </span>}
            </span>
          }/> : (summary.paymentType === billing.PaymentType.kreditna && !summary.isGratis ?
              <LoadingOverlay style={{margin: '4px 0 4px 4px'}} scale={0} builder={(action) => <AdminButton
                  style={{color: theme.content, borderColor: theme.primaryDark}}
                  background={theme.primary}
                  backgroundHover={'#ffe871'}
                  onClick={() => {
                    appStore.showDialog(
                        <YesNoDialog result={async (result) => {
                          appStore.hideDialog();
                          if (result) {
                            await action(store.participants.regenerateAdvance());
                          }
                        }}>
                          <CvvTheme.textTheme.headline5.span>
                            Ali ste prepričani, da želite ustvariti avansni račun?
                          </CvvTheme.textTheme.headline5.span>
                        </YesNoDialog>
                    );
                  }}>
                USTVARI AVANSNI RAČUN
              </AdminButton>}/> :
                  '/'
          )
      }
      </ParticipantGridCell>
      <ParticipantGridCell span={2} style={{backgroundColor: CvvColors.shade, fontWeight: 'bold'}}>
        RAČUN: {!!summary.navInvoice ?
          <div style={{display: 'inline-flex', justifyContent: 'space-between', width: '100%'}}><LoadingOverlay style={{display: 'inline-flex', marginLeft: '4px'}} scale={0} builder={(action) =>
              <span style={{cursor: 'pointer', display: 'flex', alignItems: 'center', color: theme.content}}
                    onClick={() => action(store.participants.downloadInvoicePdf(admin_participants.InvoiceType.INVOICE_TYPE_FINAL))}>
            <MdPictureAsPdf style={{marginRight: '4px'}}/>{summary.navInvoice}
          </span>
          }/>
            {summary.paymentType === PaymentType.kreditna && <LoadingOverlay scale={0} builder={(action) => <AdminButton
                style={{color: theme.content, borderColor: theme.primaryDark,}}
                background={theme.primary}
                backgroundHover={'#ffe871'}
                onClick={async () => {
                  const response = await action(store.api.sendMessage(proto.TxAdminSendRacun.create({
                    sessionId: summary.sessionId
                  }), {ack: true}));
                  if (response.status === SocketApiAckStatus.success) {
                    toast.success('Poslano');
                    store.participants.setParticipant(summary.sessionId!);
                    store.participants.load();
                  } else {
                    toast.error(response.errorMessage ?? 'Neznana napaka');
                  }
                }}>
              POŠLJI RAČUN NA E-POŠTO
            </AdminButton>}/>}</div> : (summary.daysPresent?.length === summary.nDays && !!summary.navInvoiceAdvance) ?
              <LoadingOverlay style={{margin: '4px 0 4px 4px'}} scale={0} builder={(action) => <AdminButton
                  style={{color: theme.content, borderColor: theme.primaryDark}}
                  background={theme.primary}
                  backgroundHover={'#ffe871'}
                  onClick={() => {
                    appStore.showDialog(
                        <YesNoDialog result={async (result) => {
                          appStore.hideDialog();
                          if (result) {
                            await action(store.participants.createInvoice());
                          }
                        }}>
                          <CvvTheme.textTheme.headline5.span>
                            Ali ste prepričani, da želite ustvariti račun?
                          </CvvTheme.textTheme.headline5.span>
                        </YesNoDialog>
                    );
                  }}>
                USTVARI
              </AdminButton>
              }/> : '/'}
      </ParticipantGridCell>
    </ParticipantGrid>
    <LoadingOverlay style={{width: '100%'}}
                    builder={(action) => <ParticipantGrid columns={'50px 170px 170px 170px auto'}>
                      <ParticipantGridHeaderCell span={2}>
                        OPOMBE
                      </ParticipantGridHeaderCell>
                      <ParticipantGridHeaderCell span={3}>
                        <select value={store.participants.newComment.type?.id ?? ''}
                                onChange={(e) => store.participants.newComment.type!.id = e.target.value}>
                          {store.participants?.commentTypes?.filter((t) => t.userCanAdd).map((t) => <option
                              key={t.id!}
                              value={t.id!}>{t.label}</option>)}
                        </select>
                        <input type={'text'}
                               style={{width: '100%', margin: '4px 4px 4px 4px'}}
                               value={store.participants.newComment.text ?? ''}
                               onChange={(e) => {
                                 store.participants.newComment.text = e.target.value;
                               }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (store.participants.newComment.text) {
                              action(store.participants.saveNewComment());
                            }
                          }
                        }}
                        />
                        <AdminButton
                            disabled={!store.participants.newComment.text}
                            onClick={() => action(store.participants.saveNewComment())}>SHRANI</AdminButton>
                      </ParticipantGridHeaderCell>
                      {store.participants.comments?.map((c, i) => <Comment key={i} i={i} comment={c}/>)}
                    </ParticipantGrid>}/>
  </>
});

const CommentContainer = styled.div<{ editable?: boolean, bgColor?: string }>`
  display: contents;

  & > * {
    background-color: ${({
                           theme,
                           editable,
                           bgColor
                         }: StyledProps) => bgColor ? bgColor : editable ? undefined : theme.content5};
    color: ${({theme, editable}: StyledProps) => editable ? undefined : theme.content50};
  }

  & svg {
    color: ${({theme}: StyledProps) => theme.content};
  }
`;

const Comment = ({i, comment}: { i: number, comment: cvv_types.IComment }) => {
  const app = useContext(AppStoreContext);
  const store = useContext(AdminStoreContext) as AdminStore;
  return <CommentContainer editable={!!comment.type?.userCanAdd} bgColor={comment.type?.color ?? undefined}>
    <ParticipantGridCell>
      {i + 1}.
    </ParticipantGridCell>
    <ParticipantGridCell>
      {DateFormatters.date(new Date(comment.date!.ts as number))} - {DateFormatters.time(new Date(comment.date!.ts as number))}
    </ParticipantGridCell>
    <ParticipantGridCell>
      {comment.type?.label}
    </ParticipantGridCell>
    <ParticipantGridCell>
      {comment.user}
    </ParticipantGridCell>
    <ParticipantGridCell style={{whiteSpace: 'normal', wordBreak: 'break-word', height: 'auto', alignItems: 'center'}}>
      {comment.text}
      {comment.type?.userCanDelete && <span
          style={{
            margin: '0 8px 0 auto',
            cursor: 'pointer',
            height: '100%',
            display: 'flex',
            justifyContent: "center",
            alignItems: "center"
          }}
          onClick={() => app.showDialog(
              <YesNoDialog style={{textAlign: "center", fontWeight: 700, fontSize: '20px'}} result={(result) => {
                app.hideDialog();
                if (result) {
                  store.participants.deleteComment(comment);
                }
              }}>
                Ali ste prepričani, da želite izbrisati opombo?
              </YesNoDialog>
          )}>
            <MdDelete style={{fontSize: '15px'}}/>
            </span>}
    </ParticipantGridCell>
  </CommentContainer>
}

const AdminAddressPicker = observer(({address}: { address: cvv_types.IAddress }) => {
  const [addressString, setAddressString] = useState(address.address ?? '');
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const {suggestions, resetSession} = useAutocompleteSuggestions(query, SLOVENIA_AUTOCOMPLETE_OPTIONS);

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const onSelect = async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    if (!suggestion.placePrediction) return;
    setShowSuggestions(false);
    setQuery('');

    const place = suggestion.placePrediction.toPlace();
    await place.fetchFields({
      fields: ['formattedAddress', 'addressComponents', 'location'],
    });
    resetSession();

    // Preserve the legacy strict check: the first component's first type must
    // be 'street_number' (i.e. a fully-numbered address).
    const firstType = place.addressComponents?.[0]?.types?.[0];
    if (firstType !== 'street_number') {
      toast.error('izberite naslov s hišno številko');
      setTimeout(() => setAddressString(address.address ?? ''));
      return;
    }

    if (place.formattedAddress) {
      setAddressString(place.formattedAddress);
    }
    MladiVoznikStore.placeToAddress(address, place);
  };

  return <div
      ref={containerRef}
      style={{display: 'flex', flexDirection: 'column', flexGrow: '1', justifyContent: 'center', marginLeft: '8px', position: 'relative'}}>
    <input
        type={'text'}
        autoComplete={'address-line1'}
        value={addressString}
        onChange={(e) => {
          const value = e.target.value;
          if (!value) {
            address.address = '';
            address.lat = undefined;
            address.lng = undefined;
            address.city = undefined;
            address.country = undefined;
            address.line1 = undefined;
            address.postalCode = undefined;
            address.state = undefined;
          }
          setAddressString(value);
          setQuery(value);
          setShowSuggestions(true);
        }}
        onFocus={() => {
          if (suggestions.length > 0) setShowSuggestions(true);
        }}/>
    {showSuggestions && suggestions.length > 0 && (
      <ul style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 1000,
        margin: 0,
        padding: 0,
        listStyle: 'none',
        background: 'white',
        border: '1px solid #ccc',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        maxHeight: '240px',
        overflowY: 'auto',
      }}>
        {suggestions.map((s, i) => {
          const main = s.placePrediction?.mainText?.text ?? s.placePrediction?.text?.text ?? '';
          const secondary = s.placePrediction?.secondaryText?.text;
          return (
            <li key={i}
                style={{padding: '6px 10px', cursor: 'pointer', fontSize: '13px'}}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(s)}>
              <div>{main}</div>
              {secondary && <div style={{fontSize: '11px', color: '#666'}}>{secondary}</div>}
            </li>
          );
        })}
      </ul>
    )}
  </div>;
});
